'use client'

import { cn } from '@/lib/utils'
import { Input as InputShadCn } from '@/ui/Shadcn/input'
import { Label } from '@/ui/Shadcn/label'
import { Icon } from '@/ui/components'
import { RefObject, useEffect, useState } from 'react'

import { useRef } from 'react'

interface InputFileProps extends React.ComponentProps<typeof InputShadCn> {
  label?: string
  className?: string
  id?: string
  [key: string]: any
}

interface FileDropzoneProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  maxSize?: number
  fileInputRef?: RefObject<HTMLInputElement | null>
}

export function FileDropzone({
  id,
  maxSize = 4,
  accept = 'image/*',
  onChange,
  multiple,
  ...props
}: FileDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])

  const handleClick = () => {
    fileInputRef?.current?.click()
  }

  // Clear the internal file list when the surrounding form is reset
  // (form.reset() only empties the native input, not this React state).
  useEffect(() => {
    const form = fileInputRef.current?.form
    if (!form) {
      return
    }
    const handleFormReset = () => setFiles([])
    form.addEventListener('reset', handleFormReset)
    return () => form.removeEventListener('reset', handleFormReset)
  }, [])

  const syncInputFiles = (nextFiles: File[]) => {
    const input = fileInputRef.current
    if (!input) {
      return
    }

    const dataTransfer = new DataTransfer()
    nextFiles.forEach((file) => dataTransfer.items.add(file))
    input.files = dataTransfer.files

    if (multiple) {
      input.value = ''
      const updatedTransfer = new DataTransfer()
      nextFiles.forEach((file) => updatedTransfer.items.add(file))
      input.files = updatedTransfer.files
    } else if (nextFiles.length === 0) {
      input.value = ''
    }
  }

  const notifyChange = () => {
    const input = fileInputRef.current
    if (!input || !onChange) {
      return
    }

    onChange({
      target: input,
      currentTarget: input
    } as React.ChangeEvent<HTMLInputElement>)
  }

  const handleFileSelect = (
    _e: React.ChangeEvent<HTMLInputElement>,
    selectedFiles: FileList | null
  ) => {
    const incoming = Array.from(selectedFiles ?? [])
    const nextFiles = multiple ? [...files, ...incoming] : incoming

    setFiles(nextFiles)
    syncInputFiles(nextFiles)
    notifyChange()
  }

  const handleFileDelete = (index: number) => {
    const nextFiles = files.filter((_, fileIndex) => fileIndex !== index)
    setFiles(nextFiles)
    syncInputFiles(nextFiles)
    notifyChange()
  }

  return (
    <div className="">
      <div
        className="global-focus border-border hover:border-accent-200 transition-border relative flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-8 text-center duration-300"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleClick()
          }
        }}
        tabIndex={0}
      >
        <div className="flex items-center gap-3">
          <div className="bg-muted rounded-full p-3">
            <span className="block size-5">
              <Icon icon="material-symbols:upload" className="h-full w-full" />
            </span>
          </div>
          <label className="text-sm">
            <span className="text-primary font-medium">Upload</span>{' '}
            <span>or</span>{' '}
            <span className="text-primary font-medium">click</span> to browse (
            {maxSize}MB max)
          </label>
        </div>
        <input
          type="file"
          id={id}
          ref={fileInputRef}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          maxLength={maxSize * 1024 * 1024}
          tabIndex={-1}
          accept={accept}
          onChange={(e) => handleFileSelect?.(e, e.target.files ?? null)}
          multiple={!!multiple}
          {...props}
        />
      </div>
      {files?.length > 0 && (
        <div className="flex flex-col gap-2 text-sm">
          {files.map((file, index) => (
            <div
              onClick={(e) => {
                e.stopPropagation()
                handleFileDelete(index)
              }}
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="border-border transition-border hover:border-accent-200 flex cursor-pointer items-center justify-between gap-2 rounded-md border-b p-2"
            >
              <p>
                {file.name} - {Math.abs(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
              <Icon icon="material-symbols:close" className="size-4" />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function InputFile({ label, className, id, ...props }: InputFileProps) {
  return (
    <div className={cn(`relative flex flex-col`, className)}>
      {label && (
        <Label
          htmlFor={id}
          className="top-md left-md absolute text-neutral-600"
        >
          {label} {props?.required && '*'}
        </Label>
      )}
      <FileDropzone id={id} maxSize={props?.maxSize} {...props} />
    </div>
  )
}

InputFile.displayName = 'InputFile'

export default InputFile
